import { Link } from "react-router-dom";
import { SEGMENT_KEYS, SEGMENTS, TIERS } from "../services/constants";
import {
  compactNumber,
  formatRatePer1k,
  isOnline,
  regionFlag,
} from "../utils/format";
import "./SellerCard.css";

// Premium seller card — visually rich, tier-glow, hover shimmer, less
// dense than the prior version. Layout:
//   tier glow border (color-coded ★3-★6) + soft luminescence
//   ─────────────────────────────────────────────
//   [avatar] handle ★tier            🌐 region
//            @handle · BGMI/PUBGM    ● online
//   ─────────────────────────────────────────────
//   ○ ○ ○ ○   ← segment dots (filled = has stock)
//   ─────────────────────────────────────────────
//   ₹2.5/k                ★ 4.72 (612)
//   FROM RATE              RATING
export default function SellerCard({ seller }) {
  const stockedSegs = SEGMENT_KEYS.filter((k) => Number(seller.stock?.[k]) > 0);
  const online = isOnline(seller.lastActiveAt);
  const lowestRate = seller.rates?.mix?.base;
  const tierMeta = TIERS.find((t) => t.key === String(seller.tier));
  const isElite = ["4", "5", "6"].includes(String(seller.tier));
  const tierClass = tierMeta?.cssClass || "";
  const initial = (seller.displayName || seller.handle || "?")[0].toUpperCase();

  return (
    <Link to={`/seller/${seller.handle}`}
      className={`sc-card sc-tier-${seller.tier || "0"} ${isElite ? "sc-elite" : ""}`}>
      {/* Shimmer overlay — animated on hover */}
      <div className="sc-shine" aria-hidden="true" />
      {/* Tier glow halo behind the card */}
      <div className="sc-halo" aria-hidden="true" />

      <div className="sc-row sc-top">
        <div className="sc-avatar"
          style={{ backgroundImage: seller.avatarUrl ? `url(${seller.avatarUrl})` : "none" }}>
          {!seller.avatarUrl && initial}
          {online && <span className="sc-online-dot" />}
        </div>
        <div className="sc-headline">
          <div className="sc-line1">
            <span className="sc-name">{seller.displayName || seller.handle}</span>
            {tierMeta && tierMeta.key !== "unranked" && (
              <span className={`sc-tier-badge ${tierClass}`}>{tierMeta.label}</span>
            )}
          </div>
          <div className="sc-line2">
            <span className="sc-handle">@{seller.handle}</span>
            <span className="sc-dot">·</span>
            <span className={`sc-game ${seller.game === "BGMI" ? "bgmi" : "pubgm"}`}>
              {seller.game}
            </span>
          </div>
        </div>
        <div className="sc-flag" title={seller.region}>
          {regionFlag(seller.region, seller.countryCode)}
        </div>
      </div>

      <div className="sc-segments-row" title={`Offers: ${stockedSegs.map((k) => SEGMENTS.find((s) => s.key === k)?.label).join(", ") || "—"}`}>
        {SEGMENTS.map((seg) => {
          const has = Number(seller.stock?.[seg.key]) > 0;
          return (
            <span
              key={seg.key}
              className={`sc-seg-dot ${seg.cssClass} ${has ? "on" : "off"}`}
              title={`${seg.label}: ${has ? compactNumber(seller.stock[seg.key]) : "—"}`}
            />
          );
        })}
        <span className="sc-seg-count">
          {stockedSegs.length === 4 ? "All segments" :
            stockedSegs.length === 0 ? "—" :
              `${stockedSegs.length} segment${stockedSegs.length > 1 ? "s" : ""}`}
        </span>
      </div>

      <div className="sc-row sc-bottom">
        <div className="sc-rate">
          <div className="sc-rate-num">
            {lowestRate != null ? formatRatePer1k(lowestRate, seller.currency) : "DM"}
          </div>
          <div className="sc-tinylabel">FROM</div>
        </div>
        <div className="sc-reviews">
          {seller.avgRating > 0 ? (
            <>
              <div className="sc-rating-row">
                <span className="sc-star">★</span>
                <span className="sc-rating-num">{Number(seller.avgRating).toFixed(2)}</span>
              </div>
              <div className="sc-tinylabel">{compactNumber(seller.reviewCount || 0)} REVIEWS</div>
            </>
          ) : (
            <>
              <div className="sc-rating-row sc-new">NEW</div>
              <div className="sc-tinylabel">NO REVIEWS YET</div>
            </>
          )}
        </div>
      </div>
    </Link>
  );
}
